import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  MoreHorizontal,
  Clock,
  Sparkles,
} from 'lucide-react';
import { Card, Button } from '../../components/ui';
import './ProjectBoard.css';

interface Task {
  id: string;
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high';
  timeEstimate?: string;
}

interface Column {
  id: string;
  title: string;
  emoji: string;
  tasks: Task[];
}

const mockColumns: Column[] = [
  {
    id: 'ideas',
    title: 'Ideas',
    emoji: '💡',
    tasks: [
      { id: '1', title: 'Explore new synth sounds', priority: 'low' },
      { id: '2', title: 'Collaborate with visual artist', description: 'Reach out to @artist for music video' },
    ],
  },
  {
    id: 'in-progress',
    title: 'In Progress',
    emoji: '🎨',
    tasks: [
      { id: '3', title: 'Mix down track "Midnight"', priority: 'high', timeEstimate: '2h' },
      { id: '4', title: 'Write lyrics for verse 2', priority: 'medium' },
    ],
  },
  {
    id: 'review',
    title: 'Review',
    emoji: '👀',
    tasks: [
      { id: '5', title: 'Get feedback on demo', description: 'Share with producer friends' },
    ],
  },
  {
    id: 'done',
    title: 'Done',
    emoji: '✨',
    tasks: [
      { id: '6', title: 'Record guitar riff', priority: 'medium' },
      { id: '7', title: 'Set up project structure' },
    ],
  },
];

const TaskCard: React.FC<{ task: Task }> = ({ task }) => (
  <Card className="task-card">
    <div className="task-card__header">
      <h4 className="task-card__title">{task.title}</h4>
      <button className="task-card__menu">
        <MoreHorizontal size={16} />
      </button>
    </div>
    {task.description && <p className="task-card__description">{task.description}</p>}
    <div className="task-card__meta">
      {task.priority && (
        <span className={`task-card__priority task-card__priority--${task.priority}`}>
          {task.priority}
        </span>
      )}
      {task.timeEstimate && (
        <span className="task-card__time">
          <Clock size={12} />
          {task.timeEstimate}
        </span>
      )}
    </div>
  </Card>
);

const BoardColumn: React.FC<{ column: Column }> = ({ column }) => (
  <div className="board-column">
    <div className="board-column__header">
      <span className="board-column__emoji">{column.emoji}</span>
      <h3 className="board-column__title">{column.title}</h3>
      <span className="board-column__count">{column.tasks.length}</span>
    </div>
    <div className="board-column__tasks">
      {column.tasks.map((task) => (
        <TaskCard key={task.id} task={task} />
      ))}
      <button className="board-column__add">
        <Plus size={16} />
        Add task
      </button>
    </div>
  </div>
);

export const ProjectBoard: React.FC = () => {
  const [columns] = useState(mockColumns);

  return (
    <div className="project-board-screen">
      {/* Header */}
      <header className="project-board-header">
        <div className="project-board-header__top">
          <Link to="/dashboard" className="project-board-back">
            <ArrowLeft size={24} />
          </Link>
          <div className="project-board-header__actions">
            <Button variant="ghost" size="sm">
              <MoreHorizontal size={18} />
            </Button>
          </div>
        </div>
        <div className="project-board-header__info">
          <span className="project-board-header__emoji">🎵</span>
          <div className="project-board-header__text">
            <h1 className="project-board-header__title">New Album Project</h1>
            <p className="project-board-header__subtitle">Electronic / Ambient</p>
          </div>
        </div>
        <div className="project-board-header__stats">
          <div className="project-board-stat">
            <span className="project-board-stat__value">8</span>
            <span className="project-board-stat__label">Tasks</span>
          </div>
          <div className="project-board-stat">
            <span className="project-board-stat__value">3</span>
            <span className="project-board-stat__label">In Progress</span>
          </div>
          <div className="project-board-stat">
            <span className="project-board-stat__value">2</span>
            <span className="project-board-stat__label">Completed</span>
          </div>
        </div>
      </header>

      {/* Board */}
      <main className="project-board-content">
        <div className="project-board">
          {columns.map((column) => (
            <BoardColumn key={column.id} column={column} />
          ))}
        </div>
      </main>

      {/* Quick Add FAB */}
      <div className="project-board-fab">
        <Button variant="primary">
          <Sparkles size={18} />
          Quick Add
        </Button>
      </div>
    </div>
  );
};

export default ProjectBoard;
